import { useEffect, useState } from "react";
import { useQuery } from "lib/apollo/client";
import BookReviewsList from "components/BookReview/BookReviews";
import { findReviewsForBook } from "queries/books/getReviewsForBook";
import BookType from "types/BookType";
import InteractiveStars from "components/BookReview/InteractiveStars";
import LoadingSpinner from "hardcover-ui/components/LoadingSpinner";
import useCurrentUserId from "hooks/user/useCurrentUserId";
import { BookSerializersBookByline, UserBookSerializersUserBookReview } from "types/serializers";

export interface Props {
  book: BookType;
}

const generateFilter = (book, userId, minRating, maxRating, searchTerm) => {
  const newFilter = {
    book_id: { _eq: book.id },
    has_review: { _eq: true },
    user: { onboarded: { _eq: true }, status_id: { _in: [1, 2] } },
  };

  const ratingFilter: any = {};
  if (minRating !== 0.5 || maxRating !== 5) {
    ratingFilter.rating = {
      _gte: minRating,
      _lte: maxRating,
    };
  }

  const reviewFilter: any = {};
  if (searchTerm && searchTerm.length > 0) {
    reviewFilter.review = { _ilike: `%${searchTerm}%` };
  }

  const userFilter: any = {};
  if (userId) {
    userFilter.user_id = { _neq: userId };
  }

  return {
    ...newFilter,
    ...reviewFilter,
    ...userFilter,
    ...ratingFilter,
  };
};

export default function BookReviews({ book, reviews: initialReviews }:{book: BookSerializersBookByline; reviews: UserBookSerializersUserBookReview[]}) {
  const [reviews, setReviews] = useState<UserBookSerializersUserBookReview[]>(initialReviews);
  const [minRating, setMinRating] = useState(0.5);
  const [maxRating, setMaxRating] = useState(5);
  const [searchTerm, setSearchTerm] = useState(null);
  const [changes, setChanges] = useState(false);
  const currentUserId = useCurrentUserId();
  const [filter, setFilter] = useState(
    generateFilter(book, currentUserId, minRating, maxRating, searchTerm)
  );

  const { loading } = useQuery(findReviewsForBook, {
    variables: {
      filter,
      userId: currentUserId,
      includeCurrentUser: currentUserId > 0,
    },
    skip: !changes,
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      setReviews(data.reviews);
    },
  });

  useEffect(() => {
    setFilter(
      generateFilter(book, currentUserId, minRating, maxRating, searchTerm)
    );
  }, [book, currentUserId, minRating, maxRating, searchTerm]);

  return (
    <>
      <div className="my-4">
        <div className="flex flex-row justify-start sm:justify-between my-4 items-center sm:space-x-8 flex-wrap">
          <div className="flex-grow ">
            {/* <p className="dark:text-gray-500 text-gray-700">Filter reviews</p> */}
            <input
              type="text"
              placeholder="Filter reviews that contain..."
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              className="w-full focus:ring-indigo-500 focus:border-indigo-500 block  min-w-0 rounded-md sm:text-sm border-gray-300 dark:bg-gray-600 dark:border-gray-500 dark:placeholder:text-gray-300 placeholder:text-gray-600"
            />
          </div>

          <div className="flex flex-row pt-2 items-center">
            <span className="text-sm">Filter by rating</span>
            <InteractiveStars
              rating={minRating}
              onRatingChanged={(newRating) => { setMinRating(newRating); setChanges(true); }}
            />
            <span>-</span>
            <InteractiveStars
              rating={maxRating}
              onRatingChanged={(newRating) => { setMaxRating(newRating); setChanges(true); }}
            />
          </div>
        </div>

        {loading ? (
          <LoadingSpinner variant="layout" />
        ) : (
          <>
            {reviews.length === 0 ? (
              <p className="bg-gray-300 dark:bg-gray-700 rounded p-8 text-center text-lg">
                There are no reviews for this book.
              </p>
            ) : (
              <BookReviewsList book={book} reviews={reviews} />
            )}
          </>
        )}
      </div>
    </>
  );
}
